import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppShell from '../components/AppShell';
import CreatorCard from '../components/CreatorCard';
import TrendingSection from '../components/TrendingSection';
import api from '../api';

/**
 * 03 — Home Feed (Redesigned 2026-02, v3).
 * Structure:
 *   1. Search bar (with inline dynamic "N creators" pill + Filters icon)
 *      + Trending / Featured exclusive view toggles
 *      + Streak chip (tap-to-open tooltip)
 *   2. Active filter pills (only when filters applied)
 *   3. Single 2-column creator grid — same CreatorCard everywhere.
 *      - viewMode='all'      → all creators (default)
 *      - viewMode='trending' → sorted by 7d change DESC + highlighted change chip
 *      - viewMode='featured' → only handles that are currently in featured slots
 *
 * Rationale for removing the "Latest Posts" strip: Clan posts are the
 * exclusive payoff for holding a Bit. Exposing them on Home would leak
 * that value and cannibalise the bonding-curve loop.
 */
const FILTERS = [
  { key: 'All', color: 'all' },
  { key: 'Trader', color: 'orange' },
  { key: 'Content Creator', color: 'pink' },
  { key: 'Coach', color: 'blue' },
  { key: 'Educator', color: 'green' },
  { key: 'Community Builder', color: 'purple' },
  { key: 'Influencer', color: 'yellow' },
];

export default function HomeFeed() {
  const { t } = useTranslation();
  const [creators, setCreators] = useState(null); // accumulates in 'all' mode
  const [nextOffset, setNextOffset] = useState(null); // number | null (null = exhausted or non-all mode)
  const [loadingMore, setLoadingMore] = useState(false);
  const [featured, setFeatured] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [error, setError] = useState(null);
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('All');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('all');
  const [streakOpen, setStreakOpen] = useState(false);
  const streakWrapRef = useRef(null);
  const sentinelRef = useRef(null);
  const reqIdRef = useRef(0); // race-guard for filter changes mid-fetch
  const navigate = useNavigate();

  useEffect(() => {
    if (!streakOpen) return;
    const onDown = (e) => {
      if (streakWrapRef.current && !streakWrapRef.current.contains(e.target)) {
        setStreakOpen(false);
        e.stopPropagation();
        const swallow = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        document.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => document.removeEventListener('click', swallow, { capture: true }), 300);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setStreakOpen(false); };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [streakOpen]);

  // Fetch a page. offset=0 replaces the list; higher offsets append.
  const fetchPage = async (offset, myReqId) => {
    // 'all' uses paginated infinite scroll (24 per page). 'trending' and 'featured'
    // pull a larger single page since they need the full set to sort/filter meaningfully.
    const pageLimit = viewMode === 'all' ? 24 : 100;
    const params = {
      category: category === 'All' ? undefined : category,
      verified_only: verifiedOnly || undefined,
      q: q.trim() || undefined,
      limit: pageLimit,
      offset,
    };
    if (offset > 0) setLoadingMore(true);
    try {
      const { data } = await api.get('/creators', { params });
      if (myReqId !== reqIdRef.current) return; // stale response — drop
      setCreators((prev) => (offset === 0 ? data.creators : [...(prev || []), ...data.creators]));
      setNextOffset(viewMode === 'all' ? data.next_offset : null);
    } catch (e) {
      if (myReqId === reqIdRef.current) setError('Could not load creators. Try again in a moment.');
    } finally {
      if (myReqId === reqIdRef.current) setLoadingMore(false);
    }
  };

  // Reset + fetch page 1 whenever filters or view change
  useEffect(() => {
    setError(null);
    setCreators(null);
    setNextOffset(null);
    const t = setTimeout(() => {
      reqIdRef.current += 1;
      fetchPage(0, reqIdRef.current);
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, verifiedOnly, viewMode]);

  // One-off fetches (independent of filters)
  useEffect(() => {
    let cancelled = false;
    api.get('/featured', { params: { limit: 20 } })
      .then(({ data }) => { if (!cancelled) setFeatured(data.slots || []); })
      .catch(() => { if (!cancelled) setFeatured([]); });
    api.get('/rewards/summary')
      .then(({ data }) => { if (!cancelled) setRewards(data); })
      .catch(() => { if (!cancelled) setRewards(null); });
    return () => { cancelled = true; };
  }, []);

  // Infinite-scroll observer — fires when sentinel enters viewport
  useEffect(() => {
    if (viewMode !== 'all' || !nextOffset || !sentinelRef.current) return;
    const el = sentinelRef.current;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && nextOffset !== null && !loadingMore) {
        fetchPage(nextOffset, reqIdRef.current);
      }
    }, { rootMargin: '400px' });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextOffset, loadingMore, viewMode]);

  const featuredHandles = useMemo(
    () => new Set((featured || []).map((f) => f.clan_handle)),
    [featured],
  );

  const visible = useMemo(() => {
    if (!creators) return null;
    if (viewMode === 'trending') {
      return [...creators].sort((a, b) => (b.clan?.change_7d || 0) - (a.clan?.change_7d || 0));
    }
    if (viewMode === 'featured') {
      return creators.filter((c) => featuredHandles.has(c.handle));
    }
    if (viewMode === 'filling') {
      return creators.filter((c) => {
        const cap = Number(c.clan?.supply_cap || 0);
        const minted = Number(c.clan?.bits_minted || 0);
        const soldOut = !!c.clan?.sold_out;
        return !soldOut && cap > 0 && (minted / cap) >= 0.8;
      });
    }
    return creators;
  }, [creators, viewMode, featuredHandles]);

  const activeFilterCount = (category !== 'All' ? 1 : 0) + (verifiedOnly ? 1 : 0);
  const streakDays = rewards?.streak_days || 0;
  const weekPts = rewards?.total_this_week || 0;

  const toggleView = (mode) => setViewMode((cur) => (cur === mode ? 'all' : mode));

  const trendingCount = creators ? creators.length : 0;
  const featuredCount = (featured || []).length;
  const fillingCount = useMemo(() => {
    if (!creators) return 0;
    return creators.filter((c) => {
      const cap = Number(c.clan?.supply_cap || 0);
      const minted = Number(c.clan?.bits_minted || 0);
      const soldOut = !!c.clan?.sold_out;
      return !soldOut && cap > 0 && (minted / cap) >= 0.8;
    }).length;
  }, [creators]);

  const sectionTitle = viewMode === 'trending' ? t('home.trending') : viewMode === 'featured' ? t('home.featured') : viewMode === 'filling' ? t('home.fillingFast') : t('home.discoverCreators');
  const creatorsCount = visible ? visible.length : 0;

  return (
    <AppShell activeTab="home">
      <div className="home-wrap">
        {/* ROW 1: search + view toggles + streak chip */}
        <div className="home-top-row">
          <div className="search-bar" data-testid="search-bar">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.8" />
              <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder={t('home.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="search-input"
            />
            {creators !== null && (
              <span className="search-count" data-testid="creators-count">
                {t('home.creatorsCount', { count: creatorsCount })}
              </span>
            )}
            <button
              type="button"
              className={`search-filter-btn${activeFilterCount > 0 ? ' active' : ''}`}
              onClick={() => setFiltersOpen(true)}
              aria-label="Open filters"
              aria-haspopup="dialog"
              data-testid="filters-btn"
              title="Filters"
            >
              <svg viewBox="0 0 24 24" fill="none" width="18" height="18" aria-hidden="true">
                <path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              {activeFilterCount > 0 && <span className="filters-count">{activeFilterCount}</span>}
            </button>
          </div>

          <div className="view-toggles" role="group" aria-label="View mode">
            <button
              type="button"
              className={`view-toggle trending${viewMode === 'trending' ? ' active' : ''}`}
              onClick={() => toggleView('trending')}
              aria-pressed={viewMode === 'trending'}
              data-testid="view-toggle-trending"
            >
              <span aria-hidden="true">🔥</span> {t('home.trendingShort')}
              <span className="view-toggle-count">{trendingCount}</span>
            </button>
            <button
              type="button"
              className={`view-toggle featured${viewMode === 'featured' ? ' active' : ''}`}
              onClick={() => toggleView('featured')}
              aria-pressed={viewMode === 'featured'}
              data-testid="view-toggle-featured"
              disabled={featuredCount === 0}
              title={featuredCount === 0 ? 'No featured creators yet' : undefined}
            >
              <span aria-hidden="true">⭐</span> {t('home.featuredShort')}
              <span className="view-toggle-count">{featuredCount}</span>
            </button>
            <button
              type="button"
              className={`view-toggle filling${viewMode === 'filling' ? ' active' : ''}`}
              onClick={() => toggleView('filling')}
              aria-pressed={viewMode === 'filling'}
              data-testid="view-toggle-filling"
              disabled={fillingCount === 0}
              title={fillingCount === 0 ? 'No Clans at 80%+ supply right now' : `${fillingCount} Clan${fillingCount === 1 ? '' : 's'} 80%+ minted`}
            >
              <span aria-hidden="true">🔥</span> {t('home.fillingFastShort')}
              <span className="view-toggle-count">{fillingCount}</span>
            </button>
          </div>

          <div className="streak-chip-wrap" ref={streakWrapRef} data-testid="streak-chip-wrap">
            <button
              type="button"
              className={`streak-chip${streakOpen ? ' open' : ''}`}
              onClick={() => setStreakOpen((v) => !v)}
              aria-expanded={streakOpen}
              aria-haspopup="dialog"
              aria-label="Daily streak details"
              data-testid="streak-chip"
            >
              <span aria-hidden="true">🔥</span>
              <span className="streak-chip-val">{streakDays > 0 ? `${streakDays}d` : '—'}</span>
            </button>
            {streakOpen && (
              <div className="streak-pop" role="dialog" data-testid="streak-pop">
                <div className="streak-pop-head">
                  <span className="streak-pop-flame" aria-hidden="true">🔥</span>
                  <div>
                    <div className="streak-pop-title">
                      {streakDays > 0 ? `${streakDays}-day login streak` : 'Start a streak today'}
                    </div>
                    <div className="streak-pop-sub">
                      {streakDays > 0
                        ? `Log in tomorrow to keep it going.`
                        : `Log in every day to build your streak.`}
                    </div>
                  </div>
                </div>
                <div className="streak-pop-rows">
                  <div className="streak-pop-row">
                    <span>This week</span>
                    <b data-testid="streak-pop-week">+{weekPts.toFixed(0)} pts</b>
                  </div>
                  <div className="streak-pop-row">
                    <span>Current streak</span>
                    <b data-testid="streak-pop-days">{streakDays} day{streakDays === 1 ? '' : 's'}</b>
                  </div>
                </div>
                <div className="streak-pop-hint">
                  Earn Points by logging in daily, posting in your Clan, referring friends, or trading.
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Active filter pills */}
        {activeFilterCount > 0 && (
          <div className="home-filter-row" data-testid="home-filter-row">
            {category !== 'All' && (
              <span className="filter-pill" data-testid="active-filter-category">
                {category}
                <button
                  type="button"
                  aria-label="Clear category filter"
                  onClick={() => setCategory('All')}
                  data-testid="clear-filter-category"
                >×</button>
              </span>
            )}
            {verifiedOnly && (
              <span className="filter-pill verified" data-testid="active-filter-verified">
                ✓ Verified
                <button
                  type="button"
                  aria-label="Clear verified filter"
                  onClick={() => setVerifiedOnly(false)}
                  data-testid="clear-filter-verified"
                >×</button>
              </span>
            )}
          </div>
        )}

        {/* Trending Clans — momentum-ranked leaderboard (Growth Layer GL4) */}
        <TrendingSection />

        {/* Section header */}
        <div className="section-head" id="discover-creators">
          <span className="section-title" data-testid="section-title">{sectionTitle}</span>
        </div>

        {error && (
          <div className="empty-state" data-testid="creators-error">
            <p>{error}</p>
            <button className="btn-secondary" onClick={() => setCategory((c) => c)}>Retry</button>
          </div>
        )}

        {!error && visible === null && (
          <div className="creator-grid" data-testid="creators-loading">
            {[0, 1, 2, 3].map((i) => <div key={i} className="creator-card skeleton" />)}
          </div>
        )}

        {!error && visible && visible.length === 0 && (
          <div className="empty-state" data-testid="creators-empty">
            <p>
              {viewMode === 'featured'
                ? 'No featured creators right now — check back soon.'
                : viewMode === 'filling'
                  ? 'No Clans are close to sold out right now.'
                  : 'No creators match your search yet.'}
            </p>
            <button
              className="btn-secondary"
              onClick={() => { setQ(''); setCategory('All'); setVerifiedOnly(false); setViewMode('all'); }}
            >Clear filters</button>
          </div>
        )}

        {!error && visible && visible.length > 0 && (
          <div className="creator-grid" data-testid="creators-grid">
            {visible.map((c) => (
              <CreatorCard
                key={c.handle}
                creator={c}
                onTrade={(cr) => navigate(`/trade/${cr.handle}`)}
                highlightChange={viewMode === 'trending'}
              />
            ))}
          </div>
        )}

        {/* Infinite-scroll sentinel + loader (only in 'all' mode) */}
        {viewMode === 'all' && creators && nextOffset !== null && (
          <div ref={sentinelRef} className="infinite-sentinel" data-testid="infinite-sentinel" aria-hidden="true" />
        )}
        {loadingMore && (
          <div className="infinite-loader" data-testid="infinite-loader">
            <span className="dot" /><span className="dot" /><span className="dot" />
            <span className="infinite-loader-label">Loading more…</span>
          </div>
        )}
        {viewMode === 'all' && creators && creators.length > 0 && nextOffset === null && (
          <div className="infinite-end" data-testid="infinite-end">
            You've reached the end.
          </div>
        )}
      </div>

      {filtersOpen && (
        <FiltersSheet
          category={category}
          verifiedOnly={verifiedOnly}
          onCategory={setCategory}
          onVerified={setVerifiedOnly}
          onClose={() => setFiltersOpen(false)}
        />
      )}
    </AppShell>
  );
}

function FiltersSheet({ category, verifiedOnly, onCategory, onVerified, onClose }) {
  return (
    <div className="cs-scrim" onClick={onClose} data-testid="filters-sheet">
      <div className="cs-sheet filters-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="cs-head">
          <div className="cs-title">Filters</div>
          <button className="cs-close" onClick={onClose} aria-label="Close" data-testid="filters-close">×</button>
        </div>
        <div className="cs-field">
          <label className="cs-label">Verification</label>
          <button
            type="button"
            className={`fchip verified-chip${verifiedOnly ? ' selected' : ''}`}
            onClick={() => onVerified(!verifiedOnly)}
            data-testid="filter-verified-only"
          >
            ✓ Verified only
          </button>
        </div>
        <div className="cs-field">
          <label className="cs-label">Category</label>
          <div className="filters-category-grid">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`fchip ${f.color}${category === f.key ? ' selected' : ''}`}
                onClick={() => onCategory(f.key)}
                data-testid={`filter-${f.color}`}
              >
                {f.key}
              </button>
            ))}
          </div>
        </div>
        <button className="cs-btn-primary" onClick={onClose} data-testid="filters-apply">
          Apply
        </button>
      </div>
    </div>
  );
}
